import React, {
  useContext, useEffect, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { isEmpty, omit } from 'underscore';
import { css } from '@emotion/react';

import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { fetchCohortsQuestionSet, fetchQuestionSet } from 'redux/actions/analytics';
import { AnalyticsPageType } from 'redux/schemas/app/analytics';
import { getSurveyQuestionSets } from 'redux/selectors/analytics';

import NvDropdown, { NvDropdownButtonStyle, NvDropdownTextItem } from 'shared/components/inputs/nv-dropdown';
import usePrevious from 'shared/hooks/use-previous';
import { AnalyticsContext } from '../analytics-dashboard';
import SelectCourse from './select-course';
import { config } from '@config/pendo.config.json';

type SelectSurveyProps = {
  title: string
  canEditCourse?: boolean
  disabled?: boolean
  surveyIdToExclude?: number
  formName?: string
  preCatalogId?: string
};

const styles = css`
  .bs4-dropdown-item, .bs4-dropdown {
    max-width: 700px;
  }
`;

const SelectSurvey: React.FC<SelectSurveyProps> = ({
  title,
  canEditCourse = false,
  disabled = false,
  surveyIdToExclude,
  formName,
  preCatalogId,
}) => {
  const { analyticsPageType, institutionId } = useContext(AnalyticsContext);
  const { setValue, watch, trigger } = useFormContext();

  const [isRequesting, setIsRequesting] = useState(false);

  const dispatch = useAppDispatch();

  const selectedCourseCatalogId = preCatalogId ?? watch('postCourseCatalogId');
  const surveyId = watch(formName);

  const allSurveys = useSelector(state => getSurveyQuestionSets(state, analyticsPageType, selectedCourseCatalogId));

  const prevSelectedCatalogId = usePrevious(selectedCourseCatalogId);

  useEffect(() => {
    if (selectedCourseCatalogId
      && prevSelectedCatalogId !== selectedCourseCatalogId
      && isEmpty(allSurveys)
    ) {
      if (analyticsPageType === AnalyticsPageType.COHORT_ANALYTICS) {
        setIsRequesting(true);
        dispatch(fetchCohortsQuestionSet({
          institutionId,
          catalogId: selectedCourseCatalogId,
          filterByType: 'Survey',
        })).then(() => {
          setIsRequesting(false);
        });
      } else {
        setIsRequesting(true);
        dispatch(fetchQuestionSet({
          institutionId,
          catalogId: selectedCourseCatalogId,
          filterByType: 'Survey',
        })).then(() => {
          setIsRequesting(false);
        });
      }
    }
  }, [
    allSurveys, analyticsPageType, dispatch, institutionId,
    prevSelectedCatalogId, selectedCourseCatalogId,
  ]);

  useEffect(() => {
    if (prevSelectedCatalogId && prevSelectedCatalogId !== selectedCourseCatalogId) {
      setValue(formName, null);
      setValue('questionMappings', [], { shouldDirty: true });
      trigger('questionMappings');
    }
  }, [formName, prevSelectedCatalogId, selectedCourseCatalogId, setValue, trigger]);

  const surveyOptions: NvDropdownTextItem[] = isRequesting ? [{
    type: 'text',
    text: t.TIMELINE.LOADING(),
    textClassName: 'text-regular gray-3 pointer-events-none',
    disabled: true,
    isChecked: false,
  }] : allSurveys
    // Exclude already selected survey from survey options
    ?.filter?.((survey: any) => survey.id !== surveyIdToExclude)
    ?.map?.((survey: any) => ({
      type: 'text',
      text: survey.title,
      value: survey.id,
      id: survey.id,
      isChecked: survey.id === surveyId,
      callback: () => {
        setValue(formName, survey.id);
      },
      textClassName: 'text-regular ellipsis',
    }));

  return (
    <div className='mb-4' css={styles}>
      <SelectCourse
        title={title}
        canEditCourse={canEditCourse}
        preCatalogId={preCatalogId}
      />
      <Controller
        name={formName}
        render={({ field }) => {
          const selectedSurvey = allSurveys?.find((survey: any) => survey.id === surveyId);
          const surveyTitle = selectedSurvey?.title ?? t.COURSE_ANALYTICS.PRE_POST.MODAL.SELECT_PLACEHOLDER();

          return (
            <NvDropdown
              items={surveyOptions}
              buttonStyle={NvDropdownButtonStyle.FORM}
              title={surveyTitle}
              showSelectedIndicator
              noMenuPadding
              minWidth={700}
              maxWidth={700}
              titleClass={surveyId ? 'text-regular' : 'text-regular text-gray-3'}
              disabled={disabled || !selectedCourseCatalogId}
              toggleDataQa={config.pendo.analyticsDashboard.prePost.setupModal[formName]}
              {...omit(field, ['ref'])}
            />
          );
        }}
      />
    </div>
  );
};

export default SelectSurvey;
